










import {Component, Mixins, Prop, Watch} from 'vue-property-decorator'
import ECharts from 'vue-echarts/components/ECharts.vue'
import {GithubOrganizationCollection} from '@/model/collection/GithubOrganizationCollection'
import {GithubRepositoryCollection} from '@/model/collection/GithubRepositoryCollection'
import {MixinScreenSize} from '@/components/mixins/MixinScreenSize'

// Fallback palette for repos without a colorHex
const REPO_COLORS = [
  '#4CAF50',
  '#2196F3',
  '#FF9800',
  '#9C27B0',
  '#EF5350',
  '#00BCD4',
  '#FFEB3B',
  '#795548',
  '#607D8B',
  '#EC407A',
]

@Component({
  components: {ECharts},
})
export default class CDWheelChart extends Mixins(MixinScreenSize) {
  @Prop({type: GithubOrganizationCollection, required: true})
  organizationCollection!: GithubOrganizationCollection

  @Prop({type: Number, default: null})
  selectedOrganizationId!: number | null

  @Prop({type: Object, default: () => ({})})
  commitsByOrganization!: Record<number, number>

  @Prop({type: GithubRepositoryCollection, default: null})
  repositoryCollection!: GithubRepositoryCollection | null

  @Prop({type: Number, default: null})
  selectedRepositoryId!: number | null

  get dataForOrganizations() {
    const selectedId = this.selectedOrganizationId
    const items = [...(this.organizationCollection.items || [])].sort(
      (a, b) => {
        const ca = this.commitsByOrganization[a.$id] ?? 0
        const cb = this.commitsByOrganization[b.$id] ?? 0
        if (cb !== ca) return cb - ca
        return (a.name || a.$tag || '').localeCompare(b.name || b.$tag || '')
      }
    )

    const rawValues = items.map(it => this.commitsByOrganization[it.$id] ?? 0)
    const total = rawValues.reduce(
      (acc, v) => acc + (typeof v === 'number' ? v : 0),
      0
    )

    return items.map(it => ({
      name: it.$tag,
      value: total > 0 ? this.commitsByOrganization[it.$id] ?? 0 : 1,
      id: it.$id,
      selected: selectedId !== null && it.$id === selectedId,
      itemStyle: {
        color: it.colorHex ? `#${it.colorHex}` : undefined,
      },
    }))
  }

  get dataForRepositories() {
    if (this.selectedOrganizationId === null) return []

    const items = [...(this.repositoryCollection?.items || [])]
      .sort((a, b) => {
        const ca = a.commitsCount ?? 0
        const cb = b.commitsCount ?? 0
        if (cb !== ca) return cb - ca
        return (a.name || '').localeCompare(b.name || '')
      })
      .slice(0, 10)
    if (!items.length) return []

    const total = items.reduce((acc, it) => acc + (it.commitsCount ?? 0), 0)

    return items
      .map((it, index) => ({
        name: it.name ?? String(it.$id),
        value: total > 0 ? it.commitsCount ?? 0 : 1,
        id: it.$id,
        selected: it.$id === this.selectedRepositoryId,
        itemStyle: {
          color: it.colorHex
            ? `#${it.colorHex}`
            : REPO_COLORS[index % REPO_COLORS.length],
        },
      }))
      .filter(it => it.value > 0 || total === 0)
  }

  get options() {
    const hasRepos = this.dataForRepositories.length > 0

    return {
      series: [
        {
          name: 'Organizations',
          type: 'pie',
          radius: this.dataForOrganizations.length
            ? ['83%', '93%']
            : ['79%', '89%'],
          data: this.dataForOrganizations,
          selectedMode: 'single',
          label: {show: false},
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.35)',
            },
          },
        },
        ...(hasRepos
          ? [
              {
                name: 'Repositories',
                type: 'pie',
                radius: ['70%', '81%'],
                data: this.dataForRepositories,
                selectedMode: 'single',
                label: {show: false},
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.35)',
                  },
                },
              },
            ]
          : []),
      ],
      silent: !this.isDesktop,
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        extraCssText: 'z-index: 9999;',
        formatter: (params: {name: string; seriesName: string}) => {
          if (!this.isDesktop) return ''
          if (params.seriesName === 'Repositories') {
            return `<div>Repository</div>${params.name}`
          }
          return `<div>Organization</div>${params.name}`
        },
      },
    }
  }

  onChartClick(event: {data?: {id?: number}; seriesName?: string}) {
    if (!this.isDesktop) return
    const id = (event?.data as any)?.id
    if (typeof id !== 'number') return
    if ((event as any)?.seriesName === 'Repositories') {
      this.$emit('selectRepository', id)
      return
    }
    this.$emit('selectOrganization', id)
  }

  mounted() {
    this.queueWheelChartResize()
  }

  @Watch('dataForOrganizations', {deep: true})
  @Watch('dataForRepositories', {deep: true})
  onWheelSeriesChange() {
    this.$nextTick(() => this.resizeWheelChart())
  }

  /** ECharts often measures 0×0 on mobile until a real layout pass; mirror window resize. */
  private queueWheelChartResize() {
    const run = () => this.resizeWheelChart()
    this.$nextTick(run)
    requestAnimationFrame(run)
    setTimeout(run, 50)
    setTimeout(run, 300)
  }

  private resizeWheelChart() {
    const inst = this.$refs.wheelChart as {resize?: () => void} | undefined
    if (inst && typeof inst.resize === 'function') {
      inst.resize()
    }
  }
}
